import { useAuthStore } from '@/stores/auth.js'
import { isPublicPage } from '@/utils/browser.js'
import { handleUnauthorized } from '@/utils/unauthorized-handler.js'

const DEFAULT = {
  headers: {
    'Content-Type': 'application/json'
  }
}

function _getHeaders (headers) {
  return {
    ...DEFAULT.headers,
    ...headers
  }
}

function _responseAdapter (res) {
  return res.json().then((body) => {
    const newRes = {
      ...res,
      body
    }
    if (!res.ok) {
      throw newRes.body || newRes
    }
    return newRes
  })
}

const _emptyValues = [undefined, null, '']
export function _hasValue (value) {
  if (_emptyValues.includes(value)) {
    return false
  }
  if (Array.isArray(value)) {
    return value.filter(i => !_emptyValues.includes(i))
      .length
  }
  return true
}

let authStore

export function setupHttpApi (pinia) {
  authStore = useAuthStore(pinia)
}

export function defaultHandlerConnectionError (error) {
  switch (error.code) {
    case 401: {
      if (isPublicPage()) return
      handleUnauthorized(authStore)
      break
    }
    default:
    // nothing
  }
}

export function buildUrl ({
  url,
  queryParams
}) {
  const isFullUrl = url.indexOf('http') === 0
  const urlObj = new URL((isFullUrl ? '' : document.location.origin) + url)
  const searchParams = urlObj.searchParams
  Object.entries(queryParams || {})
    .filter(([_, value]) => _hasValue(value))
    .forEach(([key, value]) => {
      const values = Array.isArray(value) ? value : [value]
      values.forEach(i => searchParams.append(key, i))
    })
  return urlObj
}

export function getDataViaApi ({
  url,
  queryParams,
  headers
}) {
  const urlObj = buildUrl({
    url,
    queryParams
  })
  return fetch(urlObj, {
    method: 'GET',
    headers: _getHeaders(headers)
  }).then(_responseAdapter)
    .catch(error => {
      defaultHandlerConnectionError(error)
      throw error
    })
}

export function postDataViaApi ({
  url,
  queryParams,
  headers,
  body
}) {
  const urlObj = buildUrl({
    url,
    queryParams
  })
  return fetch(urlObj, {
    method: 'POST',
    headers: _getHeaders(headers),
    body: JSON.stringify(body)
  }).then(_responseAdapter)
    .catch(error => {
      defaultHandlerConnectionError(error)
      throw error
    })
}

export function postRefreshTokenDataViaApi ({
  url,
  queryParams,
  headers,
  body
}) {
  const urlObj = buildUrl({
    url,
    queryParams
  })
  return fetch(urlObj, {
    method: 'POST',
    headers: _getHeaders(headers),
    body: JSON.stringify(body)
  }).then(_responseAdapter)
    .catch(error => {
      throw error
    })
}

export function putDataViaApi ({
  url,
  queryParams,
  headers,
  body
}) {
  const urlObj = buildUrl({
    url,
    queryParams
  })
  return fetch(urlObj, {
    method: 'PUT',
    headers: _getHeaders(headers),
    body: JSON.stringify(body)
  }).then(_responseAdapter)
    .catch(error => {
      defaultHandlerConnectionError(error)
      throw error
    })
}

export function deleteDataViaApi ({
  url,
  queryParams,
  headers,
  body
}) {
  const urlObj = buildUrl({
    url,
    queryParams
  })
  return fetch(urlObj, {
    method: 'DELETE',
    headers: _getHeaders(headers),
    body: body && JSON.stringify(body)
  }).then(_responseAdapter)
    .catch(error => {
      defaultHandlerConnectionError(error)
      throw error
    })
}

export function requestDataViaExternalApi ({
  url,
  method,
  queryParams,
  headers,
  body
}) {
  const urlObj = buildUrl({
    url,
    queryParams
  })
  return fetch(urlObj, {
    method,
    headers: _getHeaders(headers),
    body: JSON.stringify(body)
  }).then(_responseAdapter)
    .catch(error => {
      defaultHandlerConnectionError(error)
      throw error
    })
}

export default {
  setupHttpApi,
  getDataViaApi,
  postDataViaApi,
  postRefreshTokenDataViaApi,
  putDataViaApi,
  deleteDataViaApi,
  requestDataViaExternalApi
}
